/**
 * SERVICE WORKERS THAT ARTIFACTS LEFT ON THE APP'S ORIGIN — owner, 2026-10-05: artifact
 * pages move to a hostname of their own.
 *
 * While artifacts were served on the App's own origin, a page could register a service
 * worker there, scoped under `/artifacts/<hash>/`, and a worker outlives the page that
 * registered it. After the move a visit to the old address is answered by that worker
 * BEFORE the request leaves the browser — so the relay's redirect to the new host is never
 * seen, and the old page keeps running on the App's origin: a stale copy if it meant well,
 * and exactly the thing the move was for if it did not. Only script on THIS origin can
 * remove it, so the App does, once, as it boots.
 *
 * THE APP REGISTERS NO WORKER OF ITS OWN, and nothing of the App's is served under
 * `/artifacts/`. So every registration on this origin is one of two kinds:
 *
 *   scoped under `/artifacts/`   an artifact's own. ONLY ONCE ARTIFACTS HAVE MOVED: where
 *                                they are still served on this origin it belongs to a page
 *                                that is still in use here, and taking it away would break
 *                                that page for nothing. So `moved` is asked first — and
 *                                only when there is one to remove, which is why an
 *                                ordinary boot adds no request at all.
 *   scoped anywhere else         NOBODY'S. It can only have been planted through an
 *                                artifact page (the relay used to serve a worker's script
 *                                from the origin's root for one; it refuses that now), and
 *                                its reach is the App itself — seen in a browser,
 *                                2026-10-05: such a worker answered a request the App's own
 *                                page made. The relay cannot retire it; only script on this
 *                                origin can. Removed ALWAYS, wherever artifacts live, and
 *                                without asking anyone.
 *
 * IF THE APP EVER GETS A WORKER OF ITS OWN, this sweep must learn its scope first, or it
 * removes that worker on every boot (retireWorkers.test.ts pins "nobody's" as it is today).
 *
 * FEATURE-DETECTED, AND IT NEVER THROWS: a shell's web view may have no service workers,
 * and this must not be the thing that stops the app from starting.
 */

export async function retireArtifactWorkers(moved: () => Promise<boolean>): Promise<number> {
  try {
    const workers = (globalThis.navigator as Navigator | undefined)?.serviceWorker
    if (!workers || typeof workers.getRegistrations !== 'function') return 0
    const all = await workers.getRegistrations()
    const stale = all.filter(underArtifacts)
    // Nobody's first, and whatever the answer below turns out to be — or fails to be.
    const planted = await unregisterAll(all.filter((r) => !underArtifacts(r)))
    if (stale.length === 0) return planted
    let gone = false
    try {
      gone = await moved()
    } catch {
      // Not knowing is not permission.
    }
    return planted + (gone ? await unregisterAll(stale) : 0)
  } catch {
    return 0
  }
}

async function unregisterAll(regs: ServiceWorkerRegistration[]): Promise<number> {
  const gone = await Promise.all(
    regs.map(async (r) => {
      try {
        return await r.unregister()
      } catch {
        // One that will not go must not keep the others.
        return false
      }
    }),
  )
  return gone.filter(Boolean).length
}

function scopePath(r: ServiceWorkerRegistration): string | null {
  try {
    return new URL(r.scope).pathname
  } catch {
    return null
  }
}

function underArtifacts(r: ServiceWorkerRegistration): boolean {
  return scopePath(r)?.startsWith('/artifacts/') ?? false
}
